class CartDiscount extends HTMLElement {
	constructor() {
		super();
		this.form = this.querySelector('form');
		this.input = this.querySelector('input[name="discount"]');
		this.button = this.querySelector('button[type="submit"]');
		this.feedback = this.querySelector('.cart-discount__feedback');
		if (this.form) {
			this.form.addEventListener('submit', this.onSubmit.bind(this));
		}
		document.addEventListener('click', this.onDocumentClick.bind(this));
	}

	getSectionsToRender() {
		return [
			{
				id: 'main-cart-items',
				section: document.getElementById('main-cart-items')?.dataset.id,
				selector: '.js-contents',
			},
			{
				id: 'main-cart-footer',
				section: document.getElementById('main-cart-footer')?.dataset.id,
				selector: '.js-contents',
			},
			{
				id: 'cart-icon-bubble',
				section: 'cart-icon-bubble',
				selector: '.shopify-section',
			},
			{
				id: 'cart-live-region-text',
				section: 'cart-live-region-text',
				selector: '.shopify-section',
			},
			{
				id: 'CartDrawer',
				section: 'cart-drawer',
				selector: '.drawer__inner',
			},
		].filter((s) => s.section && document.getElementById(s.id));
	}

	getSectionInnerHTML(html, selector) {
		const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
		return parsed ? parsed.innerHTML : '';
	}

	async onSubmit(event) {
		event.preventDefault();
		const code = (this.input.value || '').trim();
		if (!code) return;

		this.setLoading(true);
		this.setFeedback('');

		try {
			await fetch(`/discount/${encodeURIComponent(code)}`, {
				method: 'GET',
				credentials: 'same-origin',
				cache: 'no-store',
				headers: { Accept: 'text/html' },
			});

			await this.refreshCartSections();

			const cart = await this.fetchCart();
			const codeUpper = code.toUpperCase();
			const entry = (cart.discount_codes || []).find(
				(d) => d.code && d.code.toUpperCase() === codeUpper
			);
			const applied = entry ? entry.applicable !== false : false;

			if (applied) {
				this.setFeedback(this.formatString('applied', { code: entry.code }), 'success');
				this.input.value = '';
			} else if (this.looksLikeGiftCard(code)) {
				this.setFeedback(this.getString('giftCardHint'), 'info');
			} else if (entry && entry.applicable === false) {
				this.setFeedback(this.getString('notApplicable'), 'error');
			} else {
				this.setFeedback(this.getString('invalid'), 'error');
			}

			this.publishCartUpdate();
		} catch (e) {
			console.error(e);
			this.setFeedback(this.getString('error'), 'error');
		} finally {
			this.setLoading(false);
		}
	}

	onDocumentClick(event) {
		const removeBtn = event.target.closest('.discounts__remove');
		if (!removeBtn) return;
		event.preventDefault();
		this.removeDiscount(removeBtn);
	}

	async removeDiscount(removeBtn) {
		if (removeBtn.disabled) return;
		removeBtn.disabled = true;
		removeBtn.classList.add('discounts__remove--loading');
		this.setFeedback('');

		try {
			await this.refreshCartSections({ discount: '' });

			const cart = await this.fetchCart();
			if ((cart.cart_level_discount_applications || []).length === 0) {
				this.setFeedback(this.getString('removed'), 'success');
			} else {
				this.setFeedback(this.getString('removeFailed'), 'error');
			}
			this.publishCartUpdate();
		} catch (e) {
			console.error(e);
			this.setFeedback(this.getString('removeError'), 'error');
		}
	}

	getString(key) {
		const strings = window.cartDiscountStrings || {};
		return strings[key] || '';
	}

	looksLikeGiftCard(code) {
		// Shopify gift cards: 16+ alphanumeric chars, optionally grouped by dashes/spaces.
		const cleaned = code.replace(/[\s-]/g, '');
		return cleaned.length >= 16 && /^[A-Z0-9]+$/i.test(cleaned);
	}

	formatString(key, params = {}) {
		let str = this.getString(key);
		Object.keys(params).forEach((p) => {
			str = str.replace(`[${p}]`, params[p]);
		});
		return str;
	}

	async refreshCartSections(extraBody = {}) {
		const sections = this.getSectionsToRender();
		const updateUrl =
			(typeof routes !== 'undefined' && routes.cart_update_url) || '/cart/update.js';

		const body = JSON.stringify({
			...extraBody,
			sections: sections.map((s) => s.section),
			// Voir product-form.js : une URL produit localisee fait renvoyer "sections": null par Shopify.
			sections_url: (typeof routes !== 'undefined' && routes.cart_url) || '/cart',
		});

		const response = await fetch(updateUrl, {
			method: 'POST',
			credentials: 'same-origin',
			cache: 'no-store',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body,
		});

		const parsedState = await response.json();
		if (!parsedState || !parsedState.sections) return parsedState;

		sections.forEach((section) => {
			const targetEl = document.getElementById(section.id);
			if (!targetEl) return;
			const elementToReplace = targetEl.querySelector(section.selector) || targetEl;
			const newHtml = parsedState.sections[section.section];
			if (!newHtml) return;
			elementToReplace.innerHTML = this.getSectionInnerHTML(newHtml, section.selector);
		});

		return parsedState;
	}

	async fetchCart() {
		return fetch(`/cart.js?_=${Date.now()}`, {
			credentials: 'same-origin',
			cache: 'no-store',
		}).then((r) => r.json());
	}

	publishCartUpdate() {
		if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
			// source 'cart-items' so CartItems/CartDrawerItems skip their onCartUpdate
			// (which would refetch main-cart-items and clobber the drawer).
			publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
		}
	}

	setLoading(isLoading) {
		this.classList.toggle('cart-discount--loading', isLoading);
		this.button.disabled = isLoading;
		this.input.disabled = isLoading;
	}

	setFeedback(message, type) {
		this.feedback.textContent = message;
		this.feedback.classList.remove(
			'cart-discount__feedback--success',
			'cart-discount__feedback--error',
			'cart-discount__feedback--info'
		);
		if (type) {
			this.feedback.classList.add(`cart-discount__feedback--${type}`);
		}
	}
}

customElements.define('cart-discount', CartDiscount);
